// The one Call button (lead list, Call Logs row, search preview). It opens the
// one dialer pre-filled (CU-869fmbgfj); outside the dashboard layout it dials
// straight from the desk. Renders nothing unless this user can call from the
// CRM (Elevate desk or Twilio).
import PropTypes from 'prop-types';
import { Button, IconButton, Tooltip } from '@mui/material';
import Iconify from '../Iconify';
import { useOutbound } from './OutboundCallProvider';
import { useDialer } from './dialerContext';
import { isDiallable } from './outboundModel';

export default function CallButton({ to, leadId, callId, variant = 'icon', size = 'small', sx, onStarted }) {
    const outbound = useOutbound();
    const dialer = useDialer();
    const widget = Boolean(dialer?.canOpen);
    if (!(widget || outbound?.canCall) || !isDiallable(to)) return null;
    const title = !widget && outbound?.desk ? `Call ${to} from ${outbound.desk.name || 'your desk'}` : `Call ${to}`;
    const start = (e) => {
        e?.stopPropagation?.();
        if (widget) dialer.openDialer({ to, leadId: leadId ?? null, callId: callId ?? null });
        else outbound.startCall({ to, leadId: leadId ?? null });
        onStarted?.();
    };
    const busy = Boolean(widget ? dialer.isCalling : outbound?.busy);
    if (variant === 'button') {
        return (
            <Button
                size={size}
                variant="contained"
                onClick={start}
                disabled={busy}
                startIcon={<Iconify icon="solar:phone-calling-rounded-bold" width={16} />}
                data-testid="outbound-call-button"
                sx={sx}
            >
                Call
            </Button>
        );
    }
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size={size}
                    onClick={start}
                    disabled={busy}
                    aria-label={title}
                    data-testid="outbound-call-button"
                    sx={{ color: 'primary.main', p: 0.5, ...sx }}
                >
                    <Iconify icon="solar:phone-calling-rounded-bold" width={16} />
                </IconButton>
            </span>
        </Tooltip>
    );
}

CallButton.propTypes = {
    to: PropTypes.string,
    leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // A Call Logs row id (`voice:9812`): the dialer pre-fills "Call-back for missed call 19:33".
    callId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variant: PropTypes.oneOf(['icon', 'button']),
    size: PropTypes.oneOf(['small', 'medium']),
    sx: PropTypes.object,
    onStarted: PropTypes.func,
};
